import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface HomepageSection {
  id: string;
  section_type: string;
  title_ar: string | null;
  title_en: string | null;
  settings: Record<string, any>;
  content: Record<string, any>;
  sort_order: number;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface StoreTheme {
  id: string;
  name_ar: string;
  name_en: string;
  slug: string;
  description_ar: string | null;
  description_en: string | null;
  preview_image_url: string | null;
  colors: Record<string, string>;
  typography: Record<string, string>;
  layout_settings: Record<string, string>;
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface ActiveTheme {
  id: string;
  theme_id: string | null;
  custom_colors: Record<string, string>;
  custom_typography: Record<string, string>;
  custom_layout: Record<string, string>;
}

// Fetch all homepage sections
export function useHomepageSections() {
  return useQuery({
    queryKey: ['homepage-sections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('homepage_sections')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as HomepageSection[];
    }
  });
}

// Fetch all themes
export function useStoreThemes() {
  return useQuery({
    queryKey: ['store-themes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('store_themes')
        .select('*')
        .order('is_default', { ascending: false });
      
      if (error) throw error;
      return data as StoreTheme[];
    }
  });
}

// Fetch active theme
export function useActiveTheme() {
  return useQuery({
    queryKey: ['active-theme'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('active_theme')
        .select('*, store_themes(*)')
        .maybeSingle();
      
      if (error) throw error;
      return data as (ActiveTheme & { store_themes: StoreTheme | null }) | null;
    }
  });
}

// Update section
export function useUpdateSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<HomepageSection> & { id: string }) => {
      const { data, error } = await supabase
        .from('homepage_sections')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homepage-sections'] });
    }
  });
}

// Update sections order
export function useUpdateSectionsOrder() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (sections: { id: string; sort_order: number }[]) => {
      const promises = sections.map(section => 
        supabase
          .from('homepage_sections')
          .update({ sort_order: section.sort_order })
          .eq('id', section.id)
      );
      
      await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homepage-sections'] });
    }
  });
}

// Add new section
export function useAddSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (section: Omit<HomepageSection, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('homepage_sections')
        .insert(section)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homepage-sections'] });
    }
  });
}

// Delete section
export function useDeleteSection() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('homepage_sections')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homepage-sections'] });
    }
  });
}

// Apply theme
export function useApplyTheme() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (themeId: string) => {
      // Check if active_theme record exists
      const { data: existing } = await supabase
        .from('active_theme')
        .select('id')
        .single();
      
      if (existing) {
        const { error } = await supabase
          .from('active_theme')
          .update({ theme_id: themeId, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('active_theme')
          .insert({ theme_id: themeId });
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-theme'] });
    }
  });
}

// Update theme customizations
export function useUpdateThemeCustomization() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (customizations: Partial<Omit<ActiveTheme, 'id'>>) => {
      const { data: existing } = await supabase
        .from('active_theme')
        .select('id')
        .single();
      
      if (existing) {
        const { error } = await supabase
          .from('active_theme')
          .update({ ...customizations, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('active_theme')
          .insert(customizations);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-theme'] });
    }
  });
}

// Re-export section templates from config
export { SECTION_TEMPLATES, getSectionTemplate } from '@/config/sectionTemplates';
export type { SectionTemplate, SettingField } from '@/config/sectionTemplates';

// Legacy SECTION_TYPES for backwards compatibility
export const SECTION_TYPES = [
  { type: 'hero', labelAr: 'البانر الرئيسي', labelEn: 'Hero Banner', icon: 'Image' },
  { type: 'features', labelAr: 'مميزات المتجر', labelEn: 'Store Features', icon: 'CheckCircle' },
  { type: 'slider', labelAr: 'سلايدر صور', labelEn: 'Image Slider', icon: 'Layers' },
  { type: 'categories', labelAr: 'الأقسام', labelEn: 'Categories', icon: 'Grid' },
  { type: 'featured_products', labelAr: 'منتجات مميزة', labelEn: 'Featured Products', icon: 'Star' },
  { type: 'best_sellers', labelAr: 'الأكثر مبيعاً', labelEn: 'Best Sellers', icon: 'Flame' },
  { type: 'product_grid', labelAr: 'شبكة منتجات', labelEn: 'Product Grid', icon: 'LayoutGrid' },
  { type: 'banner', labelAr: 'بانر إعلاني', labelEn: 'Promotional Banner', icon: 'Megaphone' },
  { type: 'countdown', labelAr: 'عداد تنازلي', labelEn: 'Countdown Timer', icon: 'Clock' },
  { type: 'testimonials', labelAr: 'آراء العملاء', labelEn: 'Customer Reviews', icon: 'Quote' },
  { type: 'newsletter', labelAr: 'النشرة البريدية', labelEn: 'Newsletter', icon: 'Mail' },
  { type: 'spacer', labelAr: 'مسافة فارغة', labelEn: 'Spacer', icon: 'Space' },
  { type: 'custom_html', labelAr: 'HTML مخصص', labelEn: 'Custom HTML', icon: 'Code' },
];
